import { useEffect, useState } from "react";

/**
 * GoogleReviews — live reviews from the Flash Funding Google Business Profile,
 * served via Featurable (auto-syncs from Google every 24h, no API key needed).
 *
 * Usage:
 *   <GoogleReviews variant="home" />   // homepage: heading + top 3 review cards
 *   <GoogleReviews variant="full" />   // /reviews page: rating summary + all reviews
 *
 * If the fetch fails, the component renders nothing on "home" and a link to the
 * Google profile on "full" — never placeholder content.
 */

const FEATURABLE_ENDPOINT =
  "https://api.featurable.com/v1/widgets/03c4f31b-0b41-476b-8161-389a14524c4d";

const PLACE_ID = "ChIJwQV3WTHtMmERQUVB7J00mps";
const GOOGLE_PROFILE_URL = `https://www.google.com/maps/place/?q=place_id:${PLACE_ID}`;
const WRITE_REVIEW_URL = `https://search.google.com/local/writereview?placeid=${PLACE_ID}`;

const relativeTime = (iso) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 1) return "Today";
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  if (days < 30) return `${Math.floor(days / 7)} week${days < 14 ? "" : "s"} ago`;
  if (days < 365) return `${Math.floor(days / 30)} month${days < 60 ? "" : "s"} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? "" : "s"} ago`;
};

const Star = ({ filled }) => (
  <svg
    viewBox="0 0 20 20"
    className="h-4 w-4"
    fill={filled ? "#FBBC04" : "#E3D9F0"}
    aria-hidden="true"
  >
    <path d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 14.9l-5.3 2.7 1-5.8L1.5 7.7l5.9-.9L10 1.5z" />
  </svg>
);

const Stars = ({ rating }) => (
  <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} filled={i <= Math.round(rating)} />
    ))}
  </div>
);

const GoogleMark = () => (
  <span className="inline-flex items-center gap-1.5 text-xs text-[#7B758A]">
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 010-4.2V7.06H2.18a11 11 0 000 9.88l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 002.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
    </svg>
    Reviews from Google
  </span>
);

const ReviewCard = ({ review, showReply = false }) => {
  const [expanded, setExpanded] = useState(false);
  const text = review.comment || "";
  const isLong = text.length > 260;
  const shown = expanded || !isLong ? text : text.slice(0, 260).trimEnd() + "…";

  return (
    <figure className="flex h-full flex-col rounded-2xl border border-purple-200 bg-[#faf9fd] p-6">
      <div className="flex items-center justify-between">
        <Stars rating={review.starRating} />
        <span className="text-xs text-[#7B758A]">{relativeTime(review.createTime)}</span>
      </div>
      <blockquote className="mt-4 flex-1 whitespace-pre-line text-sm leading-relaxed text-[#4A4556]">
        {shown}
        {isLong && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="ml-1.5 font-medium text-[#A886D4] hover:underline"
          >
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
      </blockquote>
      {showReply && review.reviewReply?.comment && (
        <div className="mt-4 rounded-xl border-l-2 border-[#A886D4]/60 bg-[#f3edfa] p-4">
          <span className="block text-xs font-semibold uppercase tracking-wide text-[#A886D4]">
            Response from Flash Funding
          </span>
          <p className="mt-1.5 text-sm leading-relaxed text-[#4A4556]">
            {review.reviewReply.comment}
          </p>
        </div>
      )}
      <figcaption className="mt-5 flex items-center gap-3">
        {review.reviewer?.profilePhotoUrl ? (
          <img
            src={review.reviewer.profilePhotoUrl}
            alt=""
            className="h-9 w-9 rounded-full"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#A886D4]/25 text-sm font-semibold text-[#A886D4]">
            {(review.reviewer?.displayName || "G").charAt(0)}
          </span>
        )}
        <span className="text-sm font-medium text-[#1F1735]">
          {review.reviewer?.displayName || "Google user"}
        </span>
      </figcaption>
    </figure>
  );
};

export default function GoogleReviews({ variant = "home" }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    fetch(FEATURABLE_ENDPOINT)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((json) => {
        if (cancelled) return;
        if (json?.success && json.reviews?.length) {
          setData(json);
          setStatus("ready");
        } else {
          setStatus("error");
        }
      })
      .catch(() => !cancelled && setStatus("error"));
    return () => {
      cancelled = true;
    };
  }, []);

  // Homepage: fail silently — never show an empty or broken section.
  if (variant === "home" && status !== "ready") return null;

  if (variant === "home") {
    return (
      <section className="mx-auto max-w-6xl px-4 py-16" aria-labelledby="gr-heading">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 id="gr-heading" className="text-2xl font-semibold text-[#0F0A1A] sm:text-3xl">
              What our clients say
            </h2>
            <p className="mt-2 text-[#4A4556]">
              Verified reviews from our Google Business Profile.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-[#1F1735]">
                {Number(data.averageRating).toFixed(1)}
              </span>
              <Stars rating={data.averageRating} />
            </div>
            <GoogleMark />
          </div>
        </div>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {data.reviews.slice(0, 3).map((r) => (
            <ReviewCard key={r.reviewId} review={r} />
          ))}
        </div>
        <div className="mt-6 text-center">
          <a
            href={GOOGLE_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-[#A886D4] hover:underline"
          >
            Read all {data.totalReviewCount} reviews on Google →
          </a>
        </div>
      </section>
    );
  }

  // ---- Full variant (/reviews page) ----
  if (status === "loading") {
    return <div className="py-24 text-center text-[#7B758A]">Loading reviews…</div>;
  }

  if (status === "error") {
    return (
      <div className="py-24 text-center">
        <p className="text-[#4A4556]">Our reviews live on our Google Business Profile.</p>
        <a
          href={GOOGLE_PROFILE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-block font-medium text-[#A886D4] hover:underline"
        >
          Read them on Google →
        </a>
      </div>
    );
  }

  return (
    <section className="mx-auto max-w-5xl px-4 py-16" aria-labelledby="gr-full-heading">
      <header className="text-center">
        <h1 id="gr-full-heading" className="text-3xl font-semibold text-[#0F0A1A] sm:text-4xl">
          Client reviews
        </h1>
        <div className="mt-5 inline-flex flex-col items-center gap-2 rounded-2xl border border-purple-200 bg-[#faf9fd] px-8 py-5">
          <span className="text-4xl font-bold text-[#1F1735]">
            {Number(data.averageRating).toFixed(1)}
          </span>
          <Stars rating={data.averageRating} />
          <span className="text-sm text-[#4A4556]">
            Based on {data.totalReviewCount} Google review
            {data.totalReviewCount === 1 ? "" : "s"}
          </span>
          <GoogleMark />
        </div>
      </header>

      <div className="mt-10 grid gap-5 sm:grid-cols-2">
        {data.reviews.map((r) => (
          <ReviewCard key={r.reviewId} review={r} showReply />
        ))}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
        <a
          href={GOOGLE_PROFILE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full bg-[#A886D4] px-6 py-2.5 text-sm font-semibold text-[#020B1C] transition hover:bg-[#bfa2e0]"
        >
          See us on Google
        </a>
        <a
          href={WRITE_REVIEW_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full border border-purple-200 px-6 py-2.5 text-sm font-semibold text-[#1F1735] transition hover:border-[#A886D4] hover:text-[#A886D4]"
        >
          Leave us a review
        </a>
      </div>

      <p className="mt-8 text-center text-xs text-[#7B758A]">
        Reviews are sourced from Google and reflect individual customer
        experiences.
      </p>
    </section>
  );
}
